import React, { type JSX } from "react";
import { ArrowUpTrayIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { USE_CASE_LIMITATIONS } from "./chatbotConstants";

const SPINNER_CIRCLE_PATH =
  "M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z";
const ERROR_ICON_PATH =
  "M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z";
const CLOSE_ICON_PATH =
  "M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z";

/** Shown over the whole chat while files are dragged across it. */
export function HistoryLoadingOverlay({ visible }: { visible: boolean }): JSX.Element {
  return (
    <div
      className={`flex-1 flex items-center justify-center transition-opacity duration-300 ${
        visible ? "opacity-100" : "opacity-0"
      }`}
    >
      <div className="bg-white rounded-lg shadow-lg px-8 py-6 flex flex-col items-center">
        <svg className="w-8 h-8 text-cvc-blue animate-spin mb-3" fill="none" viewBox="0 0 24 24">
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          ></circle>
          <path className="opacity-75" fill="currentColor" d={SPINNER_CIRCLE_PATH}></path>
        </svg>
        <span className="text-sm text-cvc-text font-normal">Loading conversation history...</span>
      </div>
    </div>
  );
}

/** Banner shown when chat history could not be fetched. */
export function ChatHistoryErrorBanner({
  chatHistoryError,
  onDismiss,
}: {
  chatHistoryError: string;
  onDismiss: () => void;
}): JSX.Element {
  return (
    <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
      <div className="flex items-start justify-between">
        <div className="flex items-start space-x-3">
          <svg
            className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path fillRule="evenodd" d={ERROR_ICON_PATH} clipRule="evenodd" />
          </svg>
          <div className="flex-1">
            <h3 className="text-sm font-medium text-red-800">
              Failed to load conversation history
            </h3>
            <p className="mt-1 text-sm text-red-700">{chatHistoryError}</p>
          </div>
        </div>
        <button
          onClick={onDismiss}
          className="flex-shrink-0 ml-3 text-red-600 hover:text-red-800"
          aria-label="Dismiss error"
        >
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d={CLOSE_ICON_PATH} clipRule="evenodd" />
          </svg>
        </button>
      </div>
    </div>
  );
}

/** BETA bubble describing what the selected use case can and cannot do. */
export function LimitationsNotice({
  selectedUseCase,
  onDismiss,
}: {
  selectedUseCase: string;
  onDismiss: () => void;
}): JSX.Element {
  return (
    <div className="hidden sm:block px-3 sm:px-4 pb-2">
      <div
        className="relative border border-solid rounded-lg px-4 py-3 pr-10"
        style={{
          borderColor: "#E5E7EB",
          boxShadow: "0 0 20px rgba(0, 0, 0, 0.20)",
          color: "#343434",
        }}
      >
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 mb-2">
          BETA
        </span>
        <p className="text-sm leading-relaxed">{USE_CASE_LIMITATIONS[selectedUseCase]}</p>
        <button
          onClick={onDismiss}
          className="absolute top-3 right-3 p-0.5 rounded hover:bg-gray-100 transition-colors duration-200"
          aria-label="Dismiss limitations info"
        >
          <XMarkIcon className="h-4 w-4" style={{ color: "#234E81" }} />
        </button>
      </div>
    </div>
  );
}
